"use client";

import React, { createContext, useContext, useEffect, useRef } from "react";
import { useAuth } from "~/ui/hooks/useAuth";
import {
  identifyUser,
  resetUser,
  isKlaviyoLoaded,
  KlaviyoUserProperties,
} from "./lib/klaviyoClient";

interface KlaviyoContextValue {
  isLoaded: boolean;
  isIdentified: boolean;
}

const KlaviyoContext = createContext<KlaviyoContextValue>({
  isLoaded: false,
  isIdentified: false,
});

export function useKlaviyoContext() {
  return useContext(KlaviyoContext);
}

export function KlaviyoProvider({ children }: { children: React.ReactNode }) {
  const auth = useAuth();
  const [isLoaded, setIsLoaded] = React.useState(false);
  const [isIdentified, setIsIdentified] = React.useState(false);
  const lastIdentifiedEmail = useRef<string | null>(null);

  // Check if Klaviyo is loaded — uses requestIdleCallback to avoid blocking main thread
  useEffect(() => {
    if (isKlaviyoLoaded()) {
      setIsLoaded(true);
      return;
    }

    let cancelled = false;
    const startTime = Date.now();
    const TIMEOUT_MS = 15_000;

    const ric: (cb: () => void) => void =
      typeof requestIdleCallback !== "undefined"
        ? (cb) => requestIdleCallback(cb)
        : (cb) => setTimeout(cb, 1);

    const checkOnIdle = () => {
      if (cancelled) return;
      ric(() => {
        if (cancelled) return;
        if (isKlaviyoLoaded()) {
          setIsLoaded(true);
        } else if (Date.now() - startTime < TIMEOUT_MS) {
          setTimeout(checkOnIdle, 1000);
        } else {
          console.warn("Klaviyo SDK failed to load within 15 seconds");
        }
      });
    };

    checkOnIdle();
    return () => {
      cancelled = true;
    };
  }, []);

  // Handle user identification when auth state changes
  useEffect(() => {
    if (!isLoaded) return;

    const identifyCurrentUser = async () => {
      if (auth.isAuthenticated && auth.userInfos?.email) {
        const email = auth.userInfos.email;

        // Only identify if the user has changed or hasn't been identified yet
        if (lastIdentifiedEmail.current !== email) {
          const properties: KlaviyoUserProperties = {
            $email: email,
          };

          // Add optional fields if they exist
          if (auth.userInfos.firstname || auth.userInfos.given_name) {
            properties.$first_name =
              auth.userInfos.firstname || auth.userInfos.given_name;
          }

          if (auth.userInfos.lastname || auth.userInfos.family_name) {
            properties.$last_name =
              auth.userInfos.lastname || auth.userInfos.family_name;
          }

          if (auth.userInfos.phone_number) {
            properties.$phone_number = auth.userInfos.phone_number;
          }

          await identifyUser(properties);
          lastIdentifiedEmail.current = email;
          setIsIdentified(true);
        }
      } else if (!auth.isAuthenticated && lastIdentifiedEmail.current) {
        // User logged out - reset Klaviyo identity
        await resetUser();
        lastIdentifiedEmail.current = null;
        setIsIdentified(false);
      }
    };

    identifyCurrentUser();
  }, [isLoaded, auth.isAuthenticated, auth.userInfos?.email]);

  return (
    <KlaviyoContext.Provider value={{ isLoaded, isIdentified }}>
      {children}
    </KlaviyoContext.Provider>
  );
}
